import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { budgetMemberService } from '@/services/budget-member.service';
import { BudgetMemberEntry } from '@/services/types/budgets.types';
import { getCurrentUser } from '@/store/slices/auth';
import { BudgetPermission, BudgetRole, can, createBudgetPolicies, getCurrentBudgetMember } from '@/permissions';

export interface UseBudgetPermissionsOptions {
  budgetId?: string;
  members?: BudgetMemberEntry[];
  fallbackMembers?: BudgetMemberEntry[];
}

export interface UseBudgetPermissionsResult {
  role: BudgetRole | null;
  member: BudgetMemberEntry | null;
  can: (permission: BudgetPermission) => boolean;
  policies: ReturnType<typeof createBudgetPolicies>;
  isLoading: boolean;
}

export const useBudgetPermissions = ({
  budgetId,
  members: providedMembers,
  fallbackMembers = []
}: UseBudgetPermissionsOptions = {}): UseBudgetPermissionsResult => {
  const currentUser = useSelector(getCurrentUser);
  const [fetchedMembers, setFetchedMembers] = useState<BudgetMemberEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const shouldFetchMembers = Boolean(budgetId) && providedMembers === undefined;

  useEffect(() => {
    if (!shouldFetchMembers || !budgetId) {
      setFetchedMembers([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadMembers = async () => {
      setIsLoading(true);

      try {
        const { data } = await budgetMemberService.getMembers(budgetId);
        if (!cancelled) {
          setFetchedMembers(data ?? []);
        }
      } catch (error) {
        console.error('Error fetching budget members for permissions:', error);
        if (!cancelled) {
          setFetchedMembers([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadMembers();

    return () => {
      cancelled = true;
    };
  }, [budgetId, shouldFetchMembers]);

  const resolvedMembers = useMemo(() => {
    if (providedMembers !== undefined) {
      return providedMembers;
    }

    if (fetchedMembers.length > 0) {
      return fetchedMembers;
    }

    return fallbackMembers;
  }, [providedMembers, fetchedMembers, fallbackMembers]);

  const member = useMemo(() => getCurrentBudgetMember(resolvedMembers, currentUser), [resolvedMembers, currentUser]);

  const role = member?.role ?? null;

  const checkPermission = useCallback((permission: BudgetPermission) => can(role, permission), [role]);

  const policies = useMemo(
    () => createBudgetPolicies(role, member, currentUser, resolvedMembers),
    [role, member, currentUser, resolvedMembers]
  );

  return {
    role,
    member,
    can: checkPermission,
    policies,
    isLoading: shouldFetchMembers ? isLoading : false
  };
};
